import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  SiteConfigContext,
  DataServiceContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  DATA_SOURCE_KEY_MAPPING,
  getDataSourceByKeys,
  getWebsiteSourceDataByKey,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
  getTimeZoneOffset
} from '@bingads-webui-campaign-react/smart-component-util';

import { proxyReadHoursConfig } from '../utils/proxy-read-hours-config';
import { SmartBookingModal } from './booking-modal-base';
import { SmartAppointmentManagement } from './appointment-management';

const getStyles = () => ({
  content: {
    padding: '0 27px 20px',
  },
});

export const UnStyledSmartAppointmentManagementWrapper = ({
  classes,
  i18n,
  logger,
  title,
  onCloseModal,
}) => {
  const { smartPageService } = useContext(DataServiceContext);
  const { siteConfig } = useContext(SiteConfigContext);

  const [isModalVisible, setIsModalVisible] = useState(true);

  const modalStyle = useMemo(() => ({
    main: {
      width: '1075px',
      height: '480px',
      visibility: isModalVisible ? 'visible' : 'hidden',
    },
  }), [isModalVisible]);

  const onInternalModalChange = useCallback(({ isOpen, shouldCloseAllModal }) => {
    setIsModalVisible(!isOpen);

    if (shouldCloseAllModal) {
      onCloseModal();
    }
  }, [onCloseModal]);

  const appointmentHours = proxyReadHoursConfig(siteConfig);
  const smartPageId = siteConfig.id;
  const timeZoneId = getWebsiteSourceDataByKey(
    siteConfig,
    WEBSITE_SOURCE_DATA_KEY_MAPPING.TimeZoneId
  );
  const [emailAddress, businessName] = getDataSourceByKeys(
    siteConfig,
    [DATA_SOURCE_KEY_MAPPING.Emails, DATA_SOURCE_KEY_MAPPING.BusinessName],
    ['', '']
  );
  const customerEmailAddress = _.isArray(emailAddress) ? emailAddress[0] : emailAddress;
  const customerTimeZoneOffset = getTimeZoneOffset({ timeZoneId });
  // User locale should be "language + region", don't use "useCurrentLocale" as it might return "en"
  // which is invalid for a standard locale
  const userLocale = getWebsiteSourceDataByKey(
    siteConfig,
    WEBSITE_SOURCE_DATA_KEY_MAPPING.Localization
  ) || 'en-us';

  return (
    <SmartBookingModal
      isOpen
      hideHeader
      hideFooter
      className="appointment-management-modal"
      i18n={i18n}
      onCloseModal={onCloseModal}
      modalStyle={modalStyle}
    >
      <SmartAppointmentManagement
        className={classes.content}
        i18n={i18n}
        logger={logger}
        title={title}
        customerEmailAddress={customerEmailAddress}
        customerTimeZoneOffset={customerTimeZoneOffset}
        appointmentHours={appointmentHours}
        businessName={businessName}
        userLocale={userLocale}
        smartPageId={smartPageId}
        smartPageService={smartPageService}
        onInternalModalChange={onInternalModalChange}
      />
    </SmartBookingModal>
  );
};

UnStyledSmartAppointmentManagementWrapper.propTypes = {
  classes: PropTypes.shape({
    content: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  title: PropTypes.string.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logError: PropTypes.func,
    logTrace: PropTypes.func,
  }),
};

UnStyledSmartAppointmentManagementWrapper.defaultProps = {
  logger: {
    logError: _.noop,
    logTrace: _.noop,
  },
};

export const SmartAppointmentManagementWrapper = withDefaultStyles(
  UnStyledSmartAppointmentManagementWrapper,
  getStyles
);
